import React, { useRef, useEffect, useCallback } from 'react';

/**
 * Wraps table content and shows a horizontal scrollbar at the top, synced with the content below.
 * Use this so users can scroll wide tables without scrolling to the bottom first.
 */
interface TableScrollTopBarProps {
  children: React.ReactNode;
  className?: string;
  /** Optional: class for the content (table) container */
  contentClassName?: string;
}

const TableScrollTopBar: React.FC<TableScrollTopBarProps> = ({
  children,
  className = '',
  contentClassName = '',
}) => {
  const topRef = useRef<HTMLDivElement>(null);
  const topInnerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  const syncFromTop = useCallback(() => {
    if (isSyncing.current || !topRef.current || !contentRef.current) return;
    isSyncing.current = true;
    contentRef.current.scrollLeft = topRef.current.scrollLeft;
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  const syncFromContent = useCallback(() => {
    if (isSyncing.current || !topRef.current || !contentRef.current) return;
    isSyncing.current = true;
    topRef.current.scrollLeft = contentRef.current.scrollLeft;
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    const topInner = topInnerRef.current;
    if (!content || !topInner) return;

    const updateTopWidth = () => {
      const sw = content.scrollWidth;
      topInner.style.minWidth = `${sw}px`;
    };

    updateTopWidth();
    const ro = new ResizeObserver(updateTopWidth);
    ro.observe(content);

    return () => ro.disconnect();
  }, [children]);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Horizontal scrollbar at top - synced with content */}
      <div
        ref={topRef}
        onScroll={syncFromTop}
        className="overflow-x-auto overflow-y-hidden border-b border-gray-200 bg-gray-50"
        style={{ height: '14px', minHeight: '14px' }}
        aria-hidden
      >
        <div ref={topInnerRef} style={{ height: 1 }} />
      </div>
      {/* Table content - scroll synced with top bar */}
      <div
        ref={contentRef}
        onScroll={syncFromContent}
        className={`overflow-x-auto overflow-y-visible ${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default TableScrollTopBar;
